import React, { useEffect, useMemo, useRef, useState } from 'react';

import { FormInputVariants } from '#/service/constants';

import { SplitTimestampProps } from './SplitTimestamp.types';
import { dateUtcToInputValue } from './utils/calculations';

interface UseSplitTimestampProps
  extends Pick<
    SplitTimestampProps,
    | 'inputType'
    | 'validations'
    | 'errorMessages'
    | 'dataKey'
    | 'validate'
    | 'onTouchedChange'
    | 'updateInputValue'
    | 'format'
    | 'disabled'
    | 'data-testid'
  > {}

export const useSplitTimestamp = ({
  inputType,
  validations,
  errorMessages,
  dataKey,
  validate,
  onTouchedChange,
  updateInputValue,
  format,
  disabled,
  ...rest
}: UseSplitTimestampProps) => {
  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const overrideDataTestId = rest?.['data-testid'];

  const dateRange = useMemo(() => {
    if (inputType !== FormInputVariants.DATE) {
      return {};
    }

    let min = '';
    let max = '';

    const range = validations?.find(
      (validation) => validation.timestamp
    )?.timestamp;

    if (range?.min) {
      min = dateUtcToInputValue(new Date(range.min));
    }

    if (range?.max) {
      max = dateUtcToInputValue(new Date(range.max));
    }

    return {
      min,
      max,
    };
  }, [inputType, validations]);

  useEffect(() => {
    if (!errorMessages?.length) {
      inputRef.current?.setCustomValidity('');
      validate(inputRef);
    }
  }, [dataKey, errorMessages?.length, validate]);

  const handleFocusWithRef = () => {
    setIsFocused(true);
    inputRef.current?.focus();
  };

  const blurInput = () => {
    setIsFocused(false);
    onTouchedChange(true);
  };

  const handleChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    updateInputValue(dataKey, value);
    inputRef?.current?.setCustomValidity('');
    validate(inputRef, value);
  };

  const handleFocusConditionally = () => {
    if (!isFocused) {
      handleFocusWithRef();
    }
  };

  useEffect(() => {
    if (errorMessages) {
      errorMessages.forEach((errorMessage) =>
        inputRef.current?.setCustomValidity(errorMessage)
      );
    }
  }, [errorMessages]);

  const props = {
    ref: inputRef,
    format,
    disabled,
  };

  return {
    props,
    handleFocusWithRef,
    handleFocusConditionally,
    handleChange,
    blurInput,
    overrideDataTestId,
    dateRange,
  };
};
